import type { MouseEvent as ReactMouseEvent } from 'react';

import { FieldBindingBox } from './FieldBindingBox.tsx';
import { highlightStateFor } from './fieldBindingCanvas.utils.ts';
import { DrawRect, Overlay } from './FieldBindingCanvas.styled.ts';
import {
	browserToPdf,
	pdfToBrowser,
} from '../../SignaturePlacement/PlacementCanvas/placement.utils.ts';
import type { PageDimensionsPt } from '../../SignaturePlacement/PlacementCanvas/PlacementCanvas.types.ts';
import type { BrowserBox, DrawState, FieldBindingRes, PdfBox } from './FieldBindingCanvas.types.ts';

interface Props {
	pageNum: number;
	dims: PageDimensionsPt;
	scale: number;
	bindings: FieldBindingRes[];
	selectedFieldKey: string | null;
	draw: DrawState | null;
	/**
	 * False when another surface owns the page.
	 *
	 * The two overlays sit on ONE document now, so both are always drawn — an author binding a
	 * field can see where the signatures will land, and the other way round. Only one may be
	 * dragged at a time though, or a click on a stacked box goes to whichever happens to be on
	 * top. The inactive one keeps its boxes visible and stops taking pointer events.
	 */
	interactive: boolean;
	onSelect: (fieldKey: string) => void;
	onBoxChange: (fieldKey: string, pageNum: number, pdfBox: PdfBox) => void;
	onRemove: (fieldKey: string) => void;
	onMouseDown: (pageNum: number) => (event: ReactMouseEvent<HTMLDivElement>) => void;
	onMouseMove: (event: ReactMouseEvent<HTMLDivElement>) => void;
	onFinishDraw: () => void;
}

/** The bound-field boxes for one page, and the drag-to-draw rectangle in progress. */
export const FieldBindingOverlay = ({
	pageNum,
	dims,
	scale,
	bindings,
	selectedFieldKey,
	draw,
	interactive,
	onSelect,
	onBoxChange,
	onRemove,
	onMouseDown,
	onMouseMove,
	onFinishDraw,
}: Props) => {
	const drawRect =
		draw && draw.pageNum === pageNum
			? {
					left: Math.min(draw.startX, draw.currentX),
					top: Math.min(draw.startY, draw.currentY),
					width: Math.abs(draw.currentX - draw.startX),
					height: Math.abs(draw.currentY - draw.startY),
				}
			: null;

	return (
		<Overlay
			$width={dims.widthPt * scale}
			$height={dims.heightPt * scale}
			$inert={!interactive}
			data-testid={`field-binding-overlay-${pageNum}`}
			{...(interactive
				? {
						onMouseDown: onMouseDown(pageNum),
						onMouseMove,
						onMouseUp: onFinishDraw,
						onMouseLeave: onFinishDraw,
					}
				: {})}
		>
			{bindings.map((binding) => {
				const browserBox = pdfToBrowser(
					{
						x: binding.x,
						y: binding.y,
						width: binding.width,
						height: binding.height,
					},
					dims.heightPt,
					scale
				);
				const handleChange = (next: BrowserBox) =>
					onBoxChange(
						binding.fieldKey,
						pageNum,
						browserToPdf(next, dims.heightPt, scale)
					);
				return (
					<FieldBindingBox
						key={binding.fieldKey}
						label={binding.label}
						state={highlightStateFor(binding)}
						browserBox={browserBox}
						isSelected={selectedFieldKey === binding.fieldKey}
						bounds={{
							width: dims.widthPt * scale,
							height: dims.heightPt * scale,
						}}
						onChange={handleChange}
						onSelect={() => onSelect(binding.fieldKey)}
						onRemove={() => onRemove(binding.fieldKey)}
					/>
				);
			})}
			{drawRect && (
				<DrawRect
					style={{
						left: drawRect.left,
						top: drawRect.top,
						width: drawRect.width,
						height: drawRect.height,
					}}
				/>
			)}
		</Overlay>
	);
};
